import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { ResponsivePie } from '@nivo/pie'
import { useFirmReportData } from '../../../api/coreData'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import { useDenaliFilter } from './filters/DenaliFilterContextProvider'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => {
    return formatter(d, valueFormat)
  }, [formatter, valueFormat])

  return {
    valueFormatter
  }
}

const applyStyleVariant = (chartProps, styleVariant) => {
  const defaults = {}

  if (styleVariant === 'opl') {
    defaults.borderColor = 'transparent'
    defaults.innerRadius = 0.85
    defaults.enableArcLabels = false
    defaults.enableArcLinkLabels = false
    defaults.sortByValue = true
    defaults.padAngle = 1
    defaults.arcLabelSkipAngle = 20
    defaults.arcLinkLabelsSkipAngle = 12
    defaults.padding = 0.3
    defaults.arcLinkLabelsStraightLength = 0
  }

  return {
    ...defaults,
    ...(chartProps || {})
  }
}

function DenaliPieChart ({
  reportId,
  baseQuery,
  indexBy,
  valueAccessor,
  valueFormat,
  height,
  styleVariant,
  ...chartProps
}) {
  const { filter, membershipFilter } = useDenaliFilter()
  const query = useMemo(() => {
    return {
      ...baseQuery,
      membershipFilter,
      filters: {
        ...(filter || {}),
        ...(baseQuery?.filters || {})
      },
      reportId
    }
  }, [reportId, baseQuery, filter, membershipFilter])

  const { data: report, isLoading } = useFirmReportData(query)
  const pieData = useMemo(() => {
    if (!report) return []

    return report.data.map(x => ({
      id: x[indexBy],
      label: x[indexBy],
      value: x[valueAccessor]
    }))
  }, [report, indexBy, valueAccessor])
  const { valueFormatter } = useFormatters(valueFormat)
  const _restProps = useMemo(() => applyStyleVariant(chartProps, styleVariant), [chartProps, styleVariant])

  if (isLoading) return null

  return (
    <div style={{ height }}>
      <ResponsivePie
        theme={{
          text: {
            fontFamily: 'var(--font-sans) !important',
            fontSize: '12px',
            color: 'var(--text-color-main)'
          }
        }}
        data={pieData}
        valueFormat={valueFormatter}
        {..._restProps}
      />
    </div>
  )
}

DenaliPieChart.propTypes = {
  reportId: PropTypes.string,
  baseQuery: PropTypes.object,
  indexBy: PropTypes.string,
  valueAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  styleVariant: PropTypes.oneOf(['opl', 'none'])
}

DenaliPieChart.defaultProps = {
  valueFormat: 'marketValue',
  height: 300,
  styleVariant: 'none'
}

export default DenaliPieChart
